import EditIcon from "@mui/icons-material/Edit"
import {
	Box,
	Card,
	CardActions,
	CardContent,
	Grid,
	IconButton,
	Theme,
	Tooltip,
	Typography,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { format, parseISO } from "date-fns"
import { toZonedTime } from "date-fns-tz"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import { ReactComponent as WhatsAppIcon } from "../../../assets/img/whatsapp.svg"
import { AppDispatch } from "../../../store"
import { CorporatesState } from "../../../store/actions/corporates"
import { openDrawer } from "../../../store/corporates/corporate-edit-slice"

const CorporateCard = ({
	corporateInfo,
	userTimeZone,
}: {
	corporateInfo: CorporatesState
	userTimeZone: any
}) => {
	const { t } = useTranslation()
	const dispatch = useDispatch<AppDispatch>()
	const corporateCreationDate = parseISO(corporateInfo.creationDate)
	const zonedDate = toZonedTime(corporateCreationDate, userTimeZone)
	const formattedDate = format(zonedDate, "dd/MM/yyyy")
	const formattedDateTime = format(zonedDate, "dd/MM/yyyy HH:mm:ss")

	const Strikethrough = styled("div")(() => ({
		display: "inline-block",
		[`&.strikethrough`]: {
			background:
				"linear-gradient(to left top, transparent 47.75%, #333 49.5%, #333 50.5%, transparent 52.25%)",
		},
		[`&.strikethrough > svg`]: {
			position: "relative",
			filter: "grayscale(1)",
			opacity: 0.7,
		},
		[`> svg`]: {
			width: "30px",
			height: "30px",
			verticalAlign: "middle",
		},
	}))

	const PaydoCard = styled(Card)(() => ({
		width: "300px",
		margin: "16px",
		boxShadow:
			"0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
		[`& .deleted`]: {
			backgroundColor: "rgba(255, 0, 0, 0.1)",
		},
		[`& .disabled`]: {
			backgroundColor: "#F5F5F5",
		},
	}))

	const CardHeader = styled(Box)(({ theme }: { theme: Theme }) => ({
		display: "flex",
		justifyContent: "space-between",
		color: theme.palette.primary.main,
		marginBottom: "4px",
		alignItems: "flex-start",
		[`& .title`]: {
			color: theme.palette.primary.main,
		},
	}))

	const CardInfo = styled(Typography)(() => ({
		color: "#555",
		marginBottom: "8px",
	}))

	const CardHighlightedInfo = styled(Box)(({ theme }: { theme: Theme }) => ({
		display: "flex",
		justifyContent: "space-between",
		color: theme.palette.primary.main,
		alignItems: "center",
	}))

	const CardFooter = styled(CardActions)(() => ({
		backgroundColor: "#F0F0F0",
		padding: "8px",
		borderRadius: "0 0 8px 8px",
		display: "flex",
		justifyContent: "space-between",
	}))

	const GridContainer = styled(Grid)(() => ({
		display: "flex",
		alignItems: "space-beetween",
		width: "100%",
	}))

	const TextEllipsis = styled(Grid)(() => ({
		overflow: "hidden",
		whiteSpace: "nowrap",
		textOverflow: "ellipsis",
		flexGrow: 1,
		marginRight: 8,
	}))

	const GridDate = styled(Grid)(() => ({
		maxWidth: "85px",
		textAlign: "right",
	}))

	const base64Image = corporateInfo?.logo ?? undefined
	const corporateImgSrc = base64Image && atob(base64Image)

	const handleEditCorporate = (
		corporate: CorporatesState,
		tabIndex: number | undefined
	) => {
		dispatch(
			openDrawer({
				isEditMode: true,
				corporate: corporate,
				tabIndex: tabIndex ?? 0,
			})
		)
	}

	return (
		<PaydoCard
			className={
				corporateInfo?.deleted
					? "deleted"
					: corporateInfo?.enabled
						? "default"
						: "disabled"
			}
		>
			<CardContent>
				<CardHeader>
					<GridContainer container wrap="nowrap">
						<Grid item xs zeroMinWidth>
							<CardInfo className={`title `} variant="body1">
								<Tooltip title={corporateInfo.corporateSenderName}>
									<TextEllipsis>
										{corporateInfo.corporateSenderName}
									</TextEllipsis>
								</Tooltip>{" "}
								<Typography
									component="span"
									variant="subtitle2"
									color="textSecondary"
								>
									{corporateInfo.campaigns.length}
									{t("card.campaign", {
										ns: "corporate",
										postProcess: "interval",
										count: corporateInfo.campaigns.length ?? 0,
									})}
								</Typography>
								<br />
								<Typography
									onClick={() => handleEditCorporate(corporateInfo, 1)}
									component="button"
									sx={{
										background: "none!important",
										border: "none",
										padding: "0!important",
										color: "#069",
										textDecoration: "underline",
										cursor: "pointer",
									}}
									variant="subtitle2"
									color="textSecondary"
								>
									{corporateInfo.contiCorrenti.length}
									{t("card.account", {
										ns: "corporate",
										postProcess: "interval",
										count: corporateInfo.contiCorrenti.length ?? 0,
									})}
								</Typography>
							</CardInfo>
						</Grid>
						<GridDate item>
							<div style={{ marginTop: "-4px" }}>
								<img
									alt={t("logo.bank", { ns: "common" })}
									style={{
										maxWidth: "100%",
										maxHeight: "38px",
										height: "fit-content",
										verticalAlign: "middle",
									}}
									src={corporateImgSrc}
								/>
							</div>
							<Typography
								component="div"
								variant="subtitle2"
								color="textSecondary"
							>
								<Tooltip title={`creato il ${formattedDateTime}`}>
									<span>{formattedDate}</span>
								</Tooltip>
							</Typography>
						</GridDate>
					</GridContainer>
				</CardHeader>
				<Grid
					container
					gap={"8px"}
					flexWrap={"wrap"}
					justifyContent={"space-between"}
					alignItems={"center"}
				>
					<Grid item>
						<CardInfo variant="body2">
							<b>WhatsApp:</b>{" "}
							<Strikethrough
								className={corporateInfo.whatsapp ? "" : "strikethrough"}
							>
								<WhatsAppIcon />
							</Strikethrough>
						</CardInfo>
					</Grid>
					<Grid item>
						<CardInfo variant="body2">
							<b>Auto Accept</b>:{" "}
							{corporateInfo.autoAccept
								? t("yes", { ns: "common" })
								: t("no", { ns: "common" })}
						</CardInfo>
					</Grid>
					<Grid item>
						<CardInfo variant="body2">
							<b>Check Iban</b>:{" "}
							{corporateInfo.checkIban
								? t("yes", { ns: "common" })
								: t("no", { ns: "common" })}
						</CardInfo>
					</Grid>
					<Grid item>
						<CardInfo variant="body2">
							<b>Time to expire</b>: {corporateInfo.timeToExpiry ?? "N/A"}
						</CardInfo>
					</Grid>
				</Grid>
			</CardContent>
			<CardFooter>
				<Box>
					<IconButton
						color="primary"
						onClick={() => handleEditCorporate(corporateInfo, 1)}
					>
						<Tooltip title={t(`buttons.tooltip.modify`, { ns: "corporate" })}>
							<EditIcon />
						</Tooltip>
					</IconButton>
				</Box>
				<Box sx={{ display: "flex", gap: 1 }}></Box>
			</CardFooter>
		</PaydoCard>
	)
}

export default CorporateCard
